import React from 'react';
import styled from 'styled-components';
import { tokens } from '../modal-form/tokens';

/**
 * Toggle minimalista do projeto.
 *
 * Existe porque `Form.Check type="switch"` do react-bootstrap 1.x emite a marcação do Bootstrap 4
 * (`custom-control custom-switch`) e o projeto carrega o CSS do Bootstrap 5, que só conhece
 * `form-switch`. Sem regra correspondente, o switch cai para um checkbox quadrado comum.
 *
 * O input nativo continua no DOM, apenas invisível: é ele que dá foco por teclado, espaço para
 * alternar e leitura correta por leitor de tela. O desenho fica todo no track e no knob.
 */

const Wrapper = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacingSm};
  margin: 0;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.6 : 1)};
  user-select: none;
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
`;

const Track = styled.span<{ $checked: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 18px;
  border-radius: 999px;
  background: ${props => (props.$checked ? tokens.primaryColor : '#CBD2D9')};
  transition: background 0.18s ease;

  /* Anel de foco só quando a navegação é por teclado, para não piscar a cada clique de mouse. */
  ${Input}:focus-visible + & {
    box-shadow: 0 0 0 3px rgba(31, 70, 126, 0.25);
  }
`;

const Knob = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: 2px;
  left: ${props => (props.$checked ? '18px' : '2px')};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: left 0.18s ease;
`;

const Texto = styled.span<{ $checked: boolean }>`
  font-size: ${tokens.fontBase};
  font-weight: 600;
  color: ${props => (props.$checked ? tokens.primaryColor : tokens.textMuted)};
  transition: color 0.18s ease;
`;

/**
 * Linha que põe o toggle e um ícone de ajuda lado a lado.
 *
 * A ajudinha compartilhada carrega uma `margin-bottom` fixa, pensada para quando ela fica colada a
 * um <label> de campo acima do input. Numa linha centralizada essa margem empurra o ícone para cima
 * e ele desalinha do texto, então aqui ela é zerada.
 *
 * O ícone precisa ficar FORA do <label> do toggle: dentro, clicar no "?" alternaria o interruptor.
 */
export const LinhaToggleComAjuda = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacingSm};

  > span,
  > svg {
    margin-bottom: 0;
  }
`;

interface ToggleSwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: React.ReactNode;
    disabled?: boolean;
    id?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, label, disabled, id }) => (
    <Wrapper $disabled={disabled} htmlFor={id}>
        <Input
            id={id}
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={disabled}
            aria-checked={checked}
            onChange={(e) => onChange(e.target.checked)}
        />
        <Track $checked={checked}>
            <Knob $checked={checked} />
        </Track>
        {label && <Texto $checked={checked}>{label}</Texto>}
    </Wrapper>
);

export default ToggleSwitch;
